class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm && currentButtonText.includes(previousTerm)) {
      if (currentButtonText.split(previousTerm).length - 1 > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const strictSearchTerm = window.AirackerSearch.buildQuery(searchTerm);
    const queryKey = strictSearchTerm.replaceAll(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const parameters = new URLSearchParams({
      q: strictSearchTerm,
      type: 'product',
      'options[prefix]': 'none',
    });

    const searchUrl = `${window.Shopify?.routes?.root || '/'}search`;

    fetch(`${searchUrl}?${parameters.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = this.buildSearchResultsMarkup(text, searchTerm);
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  buildSearchResultsMarkup(sectionMarkup, searchTerm) {
    const source = new DOMParser().parseFromString(sectionMarkup, 'text/html');
    const sourceCards = [...source.querySelectorAll('#product-grid .grid__item')]
      .map((card) => ({
        link: card.querySelector('.card__heading a'),
        image: card.querySelector('.card__media img'),
        price: card.querySelector('.price'),
      }))
      .filter((card) => card.link)
      .slice(0, 10);
    const root = document.createElement('div');
    root.id = 'predictive-search-results';
    root.setAttribute('role', 'listbox');

    if (sourceCards.length) {
      const groups = document.createElement('div');
      groups.id = 'predictive-search-results-groups-wrapper';
      groups.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
      const group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      const heading = document.createElement('h2');
      heading.id = 'predictive-search-products';
      heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
      heading.textContent = this.dataset.productsLabel;
      const list = document.createElement('ul');
      list.id = 'predictive-search-results-products-list';
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);

      sourceCards.forEach((sourceCard, index) => {
        const item = document.createElement('li');
        item.id = `predictive-search-option-product-${index + 1}`;
        item.className = 'predictive-search__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        const link = document.createElement('a');
        link.href = sourceCard.link.getAttribute('href');
        link.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
        link.tabIndex = -1;

        if (sourceCard.image) {
          const image = document.createElement('img');
          image.className = 'predictive-search__image';
          image.src = sourceCard.image.currentSrc || sourceCard.image.getAttribute('src');
          image.alt = sourceCard.image.getAttribute('alt') || '';
          image.width = 50;
          image.height = 50;
          image.loading = 'lazy';
          link.append(image);
        }

        const content = document.createElement('div');
        content.className = 'predictive-search__item-content';
        const title = document.createElement('p');
        title.className = 'predictive-search__item-heading h5';
        title.textContent = sourceCard.link.textContent.trim();
        content.append(title);
        if (sourceCard.price) {
          const price = document.createElement('div');
          price.className = 'price';
          price.textContent = sourceCard.price.textContent.replace(/\s+/g, ' ').trim();
          content.append(price);
        }
        link.append(content);
        item.append(link);
        list.append(item);
      });

      group.append(heading, list);
      groups.append(group);
      root.append(groups);
    } else {
      const empty = document.createElement('p');
      empty.className = 'predictive-search__heading text-body';
      empty.textContent = this.dataset.noResultsTemplate.replace('[search_term]', window.AirackerSearch.normalize(searchTerm));
      root.append(empty);
    }

    const searchButtonWrapper = document.createElement('div');
    searchButtonWrapper.id = 'predictive-search-option-search-keywords';
    searchButtonWrapper.className = 'predictive-search__search-for-button';
    const searchButton = document.createElement('button');
    searchButton.type = 'submit';
    searchButton.className = 'predictive-search__item predictive-search__item--term link link--text h5 animate-arrow';
    searchButton.tabIndex = -1;
    searchButton.setAttribute('role', 'option');
    searchButton.setAttribute('aria-selected', 'false');
    const searchButtonText = document.createElement('span');
    searchButtonText.setAttribute('data-predictive-search-search-for-text', '');
    searchButtonText.textContent = this.dataset.searchForTemplate.replace('[search_term]', window.AirackerSearch.normalize(searchTerm));
    searchButton.append(searchButtonText);
    searchButtonWrapper.append(searchButton);
    root.append(searchButtonWrapper);

    const liveRegion = document.createElement('span');
    liveRegion.className = 'hidden';
    liveRegion.setAttribute('data-predictive-search-live-region-count-value', '');
    liveRegion.textContent = this.dataset.resultsTemplate.replace('[count]', String(sourceCards.length));
    root.append(liveRegion);
    return root.outerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
